import { useRef, useEffect, useCallback } from "react";

type ResizeCallback = (size: { width: number; height: number }) => void;

const useResizeObserver = <T extends Element = Element>(callback: ResizeCallback) => {
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const observedNode = useRef<T | null>(null);
  const previousDimensions = useRef({ width: 0, height: 0 });

  const handleResize = useCallback(
    (entries: ResizeObserverEntry[]) => {
      const [entry] = entries;
      if (entry) {
        const { width, height } = entry.contentRect;
        if (
          width === previousDimensions.current.width &&
          height === previousDimensions.current.height
        ) {
          return;
        }
        previousDimensions.current = { width, height };
        callback({ width, height });
      }
    },
    [callback],
  );

  const ref = useCallback(
    (node: T | null) => {
      if (observedNode.current === node) return;

      resizeObserver.current?.disconnect();
      resizeObserver.current = null;
      observedNode.current = node;

      if (!node) return;

      resizeObserver.current = new ResizeObserver(handleResize);
      resizeObserver.current.observe(node);
    },
    [handleResize],
  );

  useEffect(() => {
    return () => {
      resizeObserver.current?.disconnect();
    };
  }, []);

  return ref;
};

export default useResizeObserver;
